import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Multi-select объектов с поиском для Проектного офиса.
 */
const ProjectOfficeCitySelect = ({
  cities = [],
  selectedCities,
  includeCommon,
  onChangeCities,
  onToggleCommon,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  const selectedSet = selectedCities instanceof Set ? selectedCities : new Set(selectedCities || []);

  const filteredCities = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return cities;
    return cities.filter((c) => (c.name || '').toLowerCase().includes(q));
  }, [cities, query]);

  useEffect(() => {
    if (!open) return undefined;

    const onDocMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open]);

  const allSelected = cities.length > 0 && cities.every((c) => selectedSet.has(c.id));

  const triggerLabel = (() => {
    const n = selectedSet.size;
    const parts = [];
    if (n === 0) parts.push('Объекты не выбраны');
    else if (allSelected) parts.push('Все объекты');
    else parts.push(`Объекты: ${n}`);
    if (includeCommon) parts.push('+ общие');
    return parts.join(' ');
  })();

  const toggleCity = (cityId) => {
    const next = new Set(selectedSet);
    if (next.has(cityId)) next.delete(cityId);
    else next.add(cityId);
    onChangeCities(next);
  };

  const selectAll = () => {
    onChangeCities(new Set(cities.map((c) => c.id)));
  };

  const clearAll = () => {
    onChangeCities(new Set());
  };

  const removeCity = (cityId) => {
    const next = new Set(selectedSet);
    next.delete(cityId);
    onChangeCities(next);
  };

  const selectedCityList = cities.filter((c) => selectedSet.has(c.id));

  return (
    <div className="po-city-select" ref={rootRef}>
      <button
        type="button"
        className="btn btn-outline-secondary po-city-select__trigger"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((v) => !v)}
      >
        <span>{triggerLabel}</span>
        <span className="po-city-select__chevron" aria-hidden>{open ? '▴' : '▾'}</span>
      </button>

      {open && (
        <div className="glass-dropdown po-city-select__dropdown" role="listbox">
          <div className="po-city-select__search">
            <input
              ref={searchRef}
              type="search"
              className="form-control"
              placeholder="Поиск по названию…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="po-city-select__actions">
            <button type="button" className="btn btn-sm" onClick={selectAll} disabled={!cities.length}>
              Выбрать все
            </button>
            <button type="button" className="btn btn-sm" onClick={clearAll} disabled={selectedSet.size === 0}>
              Снять все
            </button>
          </div>
          <label className="po-city-select__option po-city-select__option--sticky">
            <input
              type="checkbox"
              checked={!!includeCommon}
              onChange={onToggleCommon}
            />
            <span>Общие задачи</span>
          </label>
          <div className="po-city-select__list">
            {filteredCities.length === 0 ? (
              <div className="po-city-select__empty">Ничего не найдено</div>
            ) : (
              filteredCities.map((city) => (
                <label key={city.id} className="po-city-select__option">
                  <input
                    type="checkbox"
                    checked={selectedSet.has(city.id)}
                    onChange={() => toggleCity(city.id)}
                  />
                  <span>{city.name}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}

      {(selectedCityList.length > 0 || includeCommon) && (
        <div className="po-city-select__tags">
          {selectedCityList.map((city) => (
            <span key={city.id} className="glass-chip glass-chip--sm po-city-select__tag">
              {city.name}
              <button
                type="button"
                className="po-city-select__tag-remove"
                title="Снять"
                onClick={() => removeCity(city.id)}
              >
                ✕
              </button>
            </span>
          ))}
          {includeCommon && (
            <span className="glass-chip glass-chip--sm po-city-select__tag">
              Общие задачи
              <button
                type="button"
                className="po-city-select__tag-remove"
                title="Снять"
                onClick={onToggleCommon}
              >
                ✕
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectOfficeCitySelect;
